import { DimensionType, SourceType } from '@aon/tws-common-ui-questions/build/lib/models/enum';
import ContentConstants from '../contentConstants';
import { ICustomDimensionEditModel, ISurveyDimension, ISurveyQuestionEditModel } from '../models';

const getSurveyDimensionsFromQuestions = (
	surveyDimensions: ISurveyDimension[],
	surveyQuestions: ISurveyQuestionEditModel[],
): ISurveyDimension[] => {
	return surveyDimensions
		.filter((d: ISurveyDimension) => d.name !== ContentConstants.DIMENSION_CATEGORIES_DEMOGRAPHIC)
		.map((d: ISurveyDimension) => {
			return {
				...d,
				shouldDisableBulkActions: false,
				shouldDisableEdit: false,
				shouldDisableDelete: false,
				surveyQuestions: d.surveyQuestions
					? surveyQuestions.filter(
							(q: ISurveyQuestionEditModel) => d.surveyQuestions?.findIndex((sq) => sq.surveyQuestionId === q.surveyQuestionId) !== -1,
					  )
					: [],
			};
		});
};

const getDemographicDimension = (demographicQuestions: ISurveyQuestionEditModel[]): ISurveyDimension => {
	return {
		code: '',
		dimensionType: DimensionType.Dimension,
		isScored: false,
		name: ContentConstants.DIMENSION_CATEGORIES_DEMOGRAPHIC,
		sourceType: SourceType.KincentricLibrary,
		surveyQuestions: demographicQuestions,
		translations: [],
		shouldDisableBulkActions: true,
		shouldDisableEdit: true,
		shouldDisableDelete: true,
	};
};

const getOrphanedQuestionDimension = (
	surveyDimensions: ISurveyDimension[],
	surveyQuestions: ISurveyQuestionEditModel[],
): ISurveyDimension => {
	const orphanedQuestions = surveyQuestions.filter(
		(q) =>
			!q.isDemographic &&
			!surveyDimensions.some((d) => d.surveyQuestions && d.surveyQuestions.some((sdq) => sdq.surveyQuestionId === q.surveyQuestionId)),
	);

	return {
		code: '',
		dimensionType: DimensionType.Dimension,
		isScored: false,
		name: ContentConstants.DIMENSION_CATEGORIES_NO_DIMENSIONS,
		sourceType: SourceType.KincentricLibrary,
		surveyQuestions: orphanedQuestions,
		translations: [],
		shouldDisableBulkActions: false,
		shouldDisableEdit: true,
		shouldDisableDelete: true,
	};
};

const getAllSurveyDimensions = (surveyDimensions: ISurveyDimension[], surveyQuestions: ISurveyQuestionEditModel[]): ISurveyDimension[] => {
	const surveyDimensionsFromQuestions = getSurveyDimensionsFromQuestions(
		surveyDimensions,
		surveyQuestions.filter((q) => !q.isDemographic),
	);
	const demographicDimension = getDemographicDimension(surveyQuestions.filter((q) => q.isDemographic));
	const orphanedQuestionDimension = getOrphanedQuestionDimension(surveyDimensions, surveyQuestions);

	const dimensions = [...surveyDimensionsFromQuestions];
	if (demographicDimension.surveyQuestions && demographicDimension.surveyQuestions.length > 0) {
		dimensions.push(demographicDimension);
	}

	dimensions.push(orphanedQuestionDimension);

	return dimensions;
};

const isSurveyQuestionEngagementQuestion = (questionCode?: string) => {
	return questionCode && /^EN000[1-6]$/i.test(questionCode);
};

const isDimensionQuestionSelectable = (surveyQuestion: ISurveyQuestionEditModel) => {
	return !isSurveyQuestionEngagementQuestion(surveyQuestion.code);
};

const getUniqueDimensionKey = (dimension: ISurveyDimension) => `${dimension.name}-${dimension.dimensionType}`;

const mapDimensionEditModelToSurveyDimension = (surveyDimensionEditModel: ICustomDimensionEditModel): ISurveyDimension => {
	return {
		dimensionId: surveyDimensionEditModel.surveyDimensionId,
		name: surveyDimensionEditModel.name,
		code: surveyDimensionEditModel.code!,
		dimensionType: surveyDimensionEditModel.dimensionType!,
		sourceType: surveyDimensionEditModel.sourceType!,
		surveyQuestions: surveyDimensionEditModel.surveyQuestions,
		isScored: surveyDimensionEditModel.isScored,
		responseOptionGroupId: undefined,
		responseOptionGroupName: surveyDimensionEditModel.responseOptionGroupName,
		sourceResponseOptionGroupId: surveyDimensionEditModel.sourceResponseOptionGroupId,
		translations: surveyDimensionEditModel.translations,
		shouldDisableBulkActions: false,
		shouldDisableEdit: false,
		shouldDisableDelete: false,
		ownerId: surveyDimensionEditModel.ownerId,
	};
};

export {
	getAllSurveyDimensions,
	isDimensionQuestionSelectable,
	isSurveyQuestionEngagementQuestion,
	getUniqueDimensionKey,
	mapDimensionEditModelToSurveyDimension,
};
